/** Histórico de tudo, com busca — só para a conta da Lara. Fonte: docs/00G §5. */
import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { AdminShell } from "../../ui/AdminShell";
import { Card, EmptyState, Skeleton } from "../../ui/Card";

const OWNER_EMAIL = "laralivia925@gmail.com";

interface AuditEntry {
  id: string;
  action: string;
  actorType: "user" | "client" | "system";
  actorName: string | null;
  entityType: string | null;
  entityId: string | null;
  ip: string | null;
  createdAt: string;
}

const ACTION_LABEL: Record<string, string> = {
  "auth.login": "Entrou (equipe)",
  "auth.login.fail": "Tentativa de login falhou (equipe)",
  "auth.client_login": "Entrou (cliente)",
  "auth.client_login.fail": "Tentativa de login falhou (cliente)",
  "auth.magic.redeem": "Entrou pelo link mágico",
  "client.create": "Cliente cadastrado",
  "client.register": "Cliente criou a própria conta",
  "client.update": "Cliente editado",
  "client.status": "Estágio do cliente alterado",
  "client.magic.create": "Link de aprovação gerado",
  "client.report.share": "Link de visualização gerado",
  "client.demo.create": "Cliente de demonstração criado",
  "invite.create": "Convite de cadastro gerado",
  "post.create": "Post criado",
  "post.send_batch": "Posts enviados para aprovação",
  "post.approve": "Post aprovado",
  "post.revise": "Revisão solicitada",
  "metric.upsert": "Métricas registradas",
  "timeline.create": "Registro adicionado à linha do tempo",
  "briefing.save": "Briefing salvo (rascunho)",
  "briefing.submit": "Briefing enviado",
  "briefing.update": "Briefing editado pela equipe",
};

function label(action: string): string {
  return ACTION_LABEL[action] ?? action;
}

const ACTOR_LABEL: Record<AuditEntry["actorType"], string> = {
  user: "Equipe", client: "Cliente", system: "Sistema",
};

export function Historico() {
  const { session } = useSession();
  const [q, setQ] = useState("");
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const debounce = useRef<number | undefined>(undefined);

  const isOwner = (session?.email ?? "").toLowerCase() === OWNER_EMAIL || session?.role === "superadmin";

  async function load(query: string) {
    const params = query.trim() === "" ? "" : `?q=${encodeURIComponent(query.trim())}`;
    setEntries(await api.get<AuditEntry[]>(`/audit${params}`).catch(() => []));
  }

  useEffect(() => {
    if (!isOwner) return;
    void load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  function onSearch(value: string) {
    setQ(value);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => void load(value), 300);
  }

  if (session !== null && !isOwner) return <Navigate to="/gestao" replace />;

  return (
    <AdminShell>
      <h1 className="type-h1 text-text-cream">Histórico</h1>
      <p className="type-body-sm mt-1 text-text-secondary">
        Tudo que você e seus clientes fazem no Unik, em ordem — só você vê esta tela.
      </p>

      <input
        value={q}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Buscar por ação, pessoa ou item…"
        className="mt-4 h-11 w-full rounded-[var(--radius-control)] border border-border-default bg-surface-sunken px-4 text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
      />

      {entries === null && (
        <div className="mt-6 flex flex-col gap-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      )}

      {entries?.length === 0 && (
        <div className="mt-8">
          <EmptyState title="Nada encontrado" description="Tente outra palavra." />
        </div>
      )}

      {entries !== null && entries.length > 0 && (
        <div className="mt-6 flex flex-col gap-2">
          {entries.map((e) => (
            <Card key={e.id} className="!p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="type-body-sm text-text-cream">{label(e.action)}</p>
                  <p className="type-caption mt-0.5 text-text-tertiary">
                    {ACTOR_LABEL[e.actorType]}
                    {e.actorName !== null ? ` · ${e.actorName}` : ""}
                    {e.entityType !== null ? ` · ${e.entityType}` : ""}
                  </p>
                </div>
                <span className="type-caption shrink-0 text-text-tertiary">
                  {new Date(e.createdAt).toLocaleString("pt-BR")}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
